import type { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import type { GridPreferences } from '../../../store/User';
import { completeColumnOrder, sortByOrderedFields } from './columnOrder';

// The layout is stored account config that more than this app writes, so each part is read as unknown.
const recordOf = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const orderedFieldsOf = (orderedFields: unknown): string[] =>
  Array.isArray(orderedFields) ? orderedFields.filter((field): field is string => typeof field === 'string') : [];

export const withAccountDefaultLayout = <T extends { field: string; defaultHidden?: boolean; hideable?: boolean }>(
  columns: T[],
  layout: GridInitialStatePro['columns']
): T[] => {
  if (!layout) return columns;

  const model = recordOf(layout.columnVisibilityModel);
  // A column its users cannot hide must not start hidden either, or nothing would bring it back.
  const withLayoutDefaults = columns.map((col) => {
    const saved = model[col.field];
    return col.hideable !== false && typeof saved === 'boolean' ? { ...col, defaultHidden: !saved } : col;
  });

  return sortByOrderedFields(withLayoutDefaults, completeColumnOrder(orderedFieldsOf(layout.orderedFields), columns));
};

const changeTimesOf = (grid: GridPreferences): Record<string, number> => {
  const times: Record<string, number> = {};
  Object.entries(recordOf(grid.visibilityChangedAt)).forEach(([field, changedAt]) => {
    const time = typeof changedAt === 'string' ? Date.parse(changedAt) : NaN;
    if (!Number.isNaN(time)) times[field] = time;
  });
  return times;
};

/** Shaped as the grid's props, so a page can spread it. */
export interface AccountVisibilityChanges {
  /** The user's settings without their show or hide of the columns the account changed later. */
  gridSettings: GridPreferences;
  /** What the user had saved for those columns, for the grid to save back unless they switch them. */
  overriddenColumns: Readonly<Record<string, boolean>>;
}

export const NO_OVERRIDDEN_COLUMNS: Readonly<Record<string, boolean>> = Object.freeze({});

const withoutOverriddenColumns = (saved: GridPreferences, account: GridPreferences): AccountVisibilityChanges => {
  const accountChanges = changeTimesOf(account);
  const userChanges = changeTimesOf(saved);
  const accountModel = recordOf(account.muiConfig?.columns?.columnVisibilityModel);
  const kept: Record<string, unknown> = {};
  const dropped: Record<string, boolean> = {};
  Object.entries(recordOf(saved.muiConfig?.columns?.columnVisibilityModel)).forEach(([field, visible]) => {
    // Dropping a choice that already matches the account would change nothing but the settings' identity.
    const isOverridden =
      accountChanges[field] !== undefined &&
      !(userChanges[field] >= accountChanges[field]) &&
      visible !== accountModel[field];
    if (isOverridden) dropped[field] = visible as boolean;
    else kept[field] = visible;
  });
  if (!Object.keys(dropped).length) return { gridSettings: saved, overriddenColumns: NO_OVERRIDDEN_COLUMNS };

  return {
    gridSettings: {
      ...saved,
      muiConfig: { ...saved.muiConfig, columns: { ...saved.muiConfig.columns, columnVisibilityModel: kept } }
    } as GridPreferences,
    overriddenColumns: dropped
  };
};

const accountSignature = (account: GridPreferences): string =>
  JSON.stringify([account.visibilityChangedAt ?? null, account.muiConfig?.columns?.columnVisibilityModel ?? null]);

const resolved = new WeakMap<GridPreferences, Map<string, AccountVisibilityChanges>>();

/**
 * A user's show or hide of a column on a tab holds until the account next changes that column, so an admin's later
 * change reaches everyone who switched it before. A saved view is left as it is, as reports run from it.
 * The same settings and account content answer the same objects, as a grid restores whenever its settings change.
 */
export const accountVisibilityChanges = (
  saved: GridPreferences,
  account: GridPreferences | undefined
): AccountVisibilityChanges => {
  if (!account || saved.customView) return { gridSettings: saved, overriddenColumns: NO_OVERRIDDEN_COLUMNS };

  const byAccount = resolved.get(saved) ?? new Map<string, AccountVisibilityChanges>();
  resolved.set(saved, byAccount);
  const signature = accountSignature(account);
  const answer = byAccount.get(signature) ?? withoutOverriddenColumns(saved, account);
  byAccount.set(signature, answer);
  return answer;
};

/** The settings of {@link accountVisibilityChanges}, for a reader that saves nothing back. */
export const withAccountVisibilityChanges = (
  saved: GridPreferences,
  account: GridPreferences | undefined
): GridPreferences => accountVisibilityChanges(saved, account).gridSettings;
